import { useState } from 'react'
import { FormProvider, UseFormReturn } from 'react-hook-form'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList } from '@factorialco/f0-react'
import {
  AdditionalCompensationFormFieldsFragment,
  BulkCreateAdditionalCompensationsPageContractFragment,
  BulkCreateAdditionalCompensationsPageEmployeeFragment,
  PayrollConceptSelectFieldFragment,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CurrencyContextProvider } from 'modules/compensations/lib/CurrencyContext'
import ReviewEmployeesModal from 'modules/contracts/components/BulkAdditionalCompensations/ReviewEmployeesModal'
import { AdditionalCompensationFormFields } from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary/_AdditionalCompensations/AdditionalCompensationForm'

interface Props {
  employees: BulkCreateAdditionalCompensationsPageEmployeeFragment[]
  contracts: BulkCreateAdditionalCompensationsPageContractFragment[]
  payrollConcepts: PayrollConceptSelectFieldFragment[]
  methods: UseFormReturn<AdditionalCompensationFormFieldsFragment>
  onRemoveEmployee: (employeeId: number) => void
}

const BulkAdditionalCompensations = ({
  employees,
  payrollConcepts,
  methods,
  onRemoveEmployee,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false)
  const [employeesModalOpen, setEmployeesModalOpen] = useState(false)

  return (
    <Box paddingX="s24" gap="s24">
      <Box flexDirection="row" gap="s24" alignItems="center">
        <Text.Normal>
          {i18n.t('contract_versions.additional_compensation.bulk_create.employees')}
        </Text.Normal>
        <Box
          flexDirection="row"
          paddingX="s6"
          paddingY="s4"
          gap="s6"
          alignItems="center"
          cursor="pointer"
          onClick={() => setEmployeesModalOpen(true)}
          borderRadius={{ all: 'abs008' }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          boxShadow={isHovered ? 's400' : undefined}
        >
          {employees.length > 0 && (
            <F0AvatarList
              avatars={employees.map((avatar) => ({
                firstName: avatar.firstName ?? '',
                lastName: avatar.lastName ?? '',
                src: buildUrl(avatar.avatar?.url),
                type: 'person',
              }))}
              size="md"
              type="person"
            />
          )}
          {isHovered && (
            <Box
              borderRadius={{ all: 'abs008' }}
              overflowX="hidden"
              overflowY="hidden"
              background="grey200"
              padding="s4"
            >
              <Icon.Medium color="grey800" icon="eye-visible" />
            </Box>
          )}
        </Box>
      </Box>
      <Box width="s400">
        <CurrencyContextProvider>
          <FormProvider {...methods}>
            <AdditionalCompensationFormFields
              payrollConcepts={payrollConcepts}
              isFirstPaymentOnCurrentMonth
            />
          </FormProvider>
        </CurrencyContextProvider>
      </Box>
      <ReviewEmployeesModal
        isOpen={employeesModalOpen}
        employeeIds={employees.map((employee) => employee.id)}
        onRemoveEmployee={onRemoveEmployee}
        onClose={() => setEmployeesModalOpen(false)}
      />
    </Box>
  )
}

export default BulkAdditionalCompensations
